import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { boundsAroundPoint, radiusMilesFromBounds, isValidLatLng } from '../../../util/distance';
import { types as sdkTypes } from '../../../util/sdkLoader';

import IconPlus from '../IconPlus/IconPlus';
import css from './SearchFiltersRail.module.css';

const { LatLng, LatLngBounds } = sdkTypes;

// Child-age buckets -- KEEP IN SYNC with the topbar search popover (same UI grouping over the numeric
// ageBandMin/ageBandMax fields). Multi-select; the query uses the SPAN of the selection (min of mins ..
// max of maxes) with the overlap rule: class.ageBandMin <= span.max AND class.ageBandMax >= span.min.
const AGE_BUCKETS = [
  { key: '0-2', label: '0–2 yrs', min: 0, max: 2 },
  { key: '3-5', label: '3–5 yrs', min: 3, max: 5 },
  { key: '6-8', label: '6–8 yrs', min: 6, max: 8 },
  { key: '9-11', label: '9–11 yrs', min: 9, max: 11 },
  { key: '12-14', label: '12–14 yrs', min: 12, max: 14 },
  { key: '15-18', label: '15–18 yrs', min: 15, max: 18 },
];
const AGE_MIN = 0;
const AGE_MAX = 18;

// "Within X miles" options (the API filters by bounds, so a radius becomes a box around the origin).
const DISTANCE_OPTIONS_MILES = [5, 10, 15, 25, 50];

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// A collapsible white filter card (the mockup's rail language: bold title row + divider + content).
// Collapsed by default (client feedback) -- the rail reads as a compact list until opened.
const FilterCard = ({ titleId, count = 0, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={css.card}>
      <button
        type="button"
        className={css.cardHeader}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className={css.cardTitle}>
          <FormattedMessage id={titleId} />
          {count > 0 ? <span className={css.cardCount}>{count}</span> : null}
        </span>
        <IconPlus isOpen={open} />
      </button>
      {open ? <div className={css.cardContent}>{children}</div> : null}
    </div>
  );
};

const CheckboxRow = ({ checked, disabled = false, onToggle, children }) => (
  <label className={classNames(css.checkboxRow, { [css.checkboxRowDisabled]: disabled })}>
    <input
      type="checkbox"
      className={css.checkbox}
      checked={checked}
      disabled={disabled}
      onChange={onToggle}
    />
    <span className={css.checkboxLabel}>{children}</span>
  </label>
);

// The selected-age SPAN from the URL's overlap params ("0,8" + "3,18" -> { min: 3, max: 8 }), or null.
const ageSpanFromParams = urlQueryParams => {
  const minParam = urlQueryParams?.pub_ageBandMin;
  const maxParam = urlQueryParams?.pub_ageBandMax;
  if (typeof minParam !== 'string' || typeof maxParam !== 'string') {
    return null;
  }
  const spanMax = Number(minParam.split(',')[1]);
  const spanMin = Number(maxParam.split(',')[0]);
  return Number.isFinite(spanMin) && Number.isFinite(spanMax) ? { min: spanMin, max: spanMax } : null;
};

/**
 * The KidHive search filter rail (desktop left column, grid variant): Category (config taxonomy ->
 * native pub_categoryLevel1), Child age (bucket checkboxes -> the ageBand overlap query), Distance
 * ("within X miles" -> bounds around the search origin), Days of week (-> pub_scheduleDays has_any),
 * plus any config-driven built-in/custom filters passed as children (price, dates, and future Console
 * listing-field filters render automatically). Fully URL-driven: selections derive from urlQueryParams
 * and every change goes through onApplyParams (merge + history push).
 *
 * @param {Object} props
 * @param {Array} props.categories config.categoryConfiguration.categories
 * @param {Object} props.urlQueryParams current (validated) URL search params
 * @param {Object?} props.origin parsed search origin ({ lat, lng }) -- the distance filter needs it
 * @param {Function} props.onApplyParams patch of query params to merge into the URL (null deletes)
 * @param {Function} props.onResetAll clear all filters
 * @param {ReactNode} props.children config-driven FilterComponents (price, dates, custom fields)
 */
const SearchFiltersRail = props => {
  const { categories = [], urlQueryParams = {}, origin, onApplyParams, onResetAll, children } = props;
  const intl = useIntl();

  // ---- Category (MULTI-select -> pub_categoryLevel1, comma-joined) ----
  // The Marketplace API accepts multiple categoryLevel1 values (verified: "a,b" returns the union),
  // and pub_categoryLevel1 is in KIDHIVE_RAIL_FILTER_PARAMS so it passes through. COMMA (not has_any:)
  // so the topbar popover -- which reads this param with split(',') -- stays in sync.
  const selectedCategories = urlQueryParams.pub_categoryLevel1
    ? parseSelectFilterOptions(urlQueryParams.pub_categoryLevel1)
    : [];
  const onToggleCategory = id => {
    const next = selectedCategories.includes(id)
      ? selectedCategories.filter(c => c !== id)
      : [...selectedCategories, id];
    onApplyParams({ pub_categoryLevel1: next.length > 0 ? next.join(',') : null });
  };

  // ---- Child age (bucket multi-select -> span overlap query) ----
  const ageSpan = ageSpanFromParams(urlQueryParams);
  const isBucketSelected = bucket => !!ageSpan && bucket.min >= ageSpan.min && bucket.max <= ageSpan.max;
  const onToggleAgeBucket = bucket => {
    const nextSelected = AGE_BUCKETS.filter(b =>
      b.key === bucket.key ? !isBucketSelected(b) : isBucketSelected(b)
    );
    if (nextSelected.length === 0) {
      onApplyParams({ pub_ageBandMin: null, pub_ageBandMax: null });
      return;
    }
    const spanMin = Math.min(...nextSelected.map(b => b.min));
    const spanMax = Math.max(...nextSelected.map(b => b.max));
    // Overlap rule: class.ageBandMin <= span.max AND class.ageBandMax >= span.min.
    onApplyParams({
      pub_ageBandMin: `${AGE_MIN},${spanMax}`,
      pub_ageBandMax: `${spanMin},${AGE_MAX}`,
    });
  };

  // ---- Distance (single-select radius -> bounds around origin) ----
  const hasOrigin = isValidLatLng(origin);
  const currentRadius = radiusMilesFromBounds(urlQueryParams.bounds);
  const isRadiusSelected = miles => currentRadius != null && Math.abs(currentRadius - miles) < 1;
  const onToggleRadius = miles => {
    if (isRadiusSelected(miles)) {
      onApplyParams({ bounds: null });
      return;
    }
    const box = boundsAroundPoint(origin, miles);
    if (box) {
      onApplyParams({
        bounds: new LatLngBounds(new LatLng(box.ne.lat, box.ne.lng), new LatLng(box.sw.lat, box.sw.lng)),
      });
    }
  };

  // ---- Days of week (multi-select -> pub_scheduleDays has_any) ----
  const selectedDays = urlQueryParams.pub_scheduleDays
    ? parseSelectFilterOptions(urlQueryParams.pub_scheduleDays)
    : [];
  const onToggleDay = day => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day];
    onApplyParams({ pub_scheduleDays: next.length > 0 ? `has_any:${next.join(',')}` : null });
  };

  return (
    <div className={css.root}>
      {categories.length > 0 ? (
        <FilterCard titleId="SearchFiltersRail.categoryTitle" count={selectedCategories.length}>
          {categories.map(category => (
            <CheckboxRow
              key={category.id}
              checked={selectedCategories.includes(category.id)}
              onToggle={() => onToggleCategory(category.id)}
            >
              {category.name}
            </CheckboxRow>
          ))}
        </FilterCard>
      ) : null}

      <FilterCard
        titleId="SearchFiltersRail.ageTitle"
        count={AGE_BUCKETS.filter(isBucketSelected).length}
      >
        {AGE_BUCKETS.map(bucket => (
          <CheckboxRow
            key={bucket.key}
            checked={isBucketSelected(bucket)}
            onToggle={() => onToggleAgeBucket(bucket)}
          >
            {bucket.label}
          </CheckboxRow>
        ))}
      </FilterCard>

      <FilterCard
        titleId="SearchFiltersRail.distanceTitle"
        count={DISTANCE_OPTIONS_MILES.some(m => isRadiusSelected(m)) ? 1 : 0}
      >
        {!hasOrigin ? (
          <p className={css.distanceHint}>
            <FormattedMessage id="SearchFiltersRail.distanceNeedsLocation" />
          </p>
        ) : null}
        {DISTANCE_OPTIONS_MILES.map(miles => (
          <CheckboxRow
            key={miles}
            checked={isRadiusSelected(miles)}
            disabled={!hasOrigin}
            onToggle={() => onToggleRadius(miles)}
          >
            {intl.formatMessage({ id: 'SearchFiltersRail.distanceOption' }, { miles })}
          </CheckboxRow>
        ))}
      </FilterCard>

      <FilterCard titleId="SearchFiltersRail.daysTitle" count={selectedDays.length}>
        {DAYS.map(day => (
          <CheckboxRow key={day} checked={selectedDays.includes(day)} onToggle={() => onToggleDay(day)}>
            <FormattedMessage id={`CreateClassSchedule.day.${day}`} />
          </CheckboxRow>
        ))}
      </FilterCard>

      {/* Config-driven filters (price, dates, future Console listing-field filters) keep their own
          internals; the rail gives each the card treatment. */}
      {children ? <div className={css.builtInFilters}>{children}</div> : null}

      <button type="button" className={css.resetAll} onClick={onResetAll}>
        <FormattedMessage id="SearchFiltersRail.resetAll" />
      </button>
    </div>
  );
};

export default SearchFiltersRail;
